import { ReactNode, useEffect } from 'react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { Button, Flex, HStack, Text } from '@chakra-ui/react'
import { MdEdit as EditIcon } from 'react-icons/md'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import SvgIcon from 'components/SvgIcon'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMemberPermissionId } from 'interfaces/group'
import routes from 'routes'
import styles from './styles.module.scss'
import { EFeatureFlags } from 'constants/featureFlags'
import useFeatureFlagValue from 'hooks/useFeatureFlagValue'

interface IHeaderBarProps {
  setIsManageMode?: (isManageMode: boolean) => void
  isManageMode?: boolean
  title: string | ReactNode
  controlBy?:
    | 'processDetail'
    | 'searchPage'
    | 'collectionPage'
    | 'collectionDetailPage'
    | 'archivePage'
    | 'processList'
    | 'groupMemberList'
    | 'userPage'
    | 'userDetailPage'
    | 'commonLibraryList'
    | 'plansPage'
    | 'workPlanDetail'
}

const HeaderBar = ({ setIsManageMode, isManageMode, title, controlBy }: IHeaderBarProps) => {
  const {
    processStore,
    searchStore,
    userStore,
    authStore,
    groupStore,
    commonLibraryStore,
    collectionStore,
    planStore
  } = useStores()
  const navigate = useNavigate()
  const { isManagePermission } = collectionStore
  const { currentUserGroupMembers, userDetail, currentUser } = userStore
  const { value: isCommonStepEnabled } = useFeatureFlagValue(EFeatureFlags.MODULAR_PROCESS, false)
  const isBasicUser = authStore?.userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER
  const isEditor = currentUserGroupMembers?.some(
    groupMember => groupMember?.permissionId === IGroupMemberPermissionId.EDITOR
  )
  const userCanManage = isEditor || !isBasicUser

  // Feature flag for work plan detail manage mode
  const { value: isWorkPlanDetailManageModeEnabled } = useFeatureFlagValue(
    EFeatureFlags.WORK_PLAN_DETAIL_MANAGE_MODE,
    true
  )

  let showManageButton = false
  let isManageModeDefault = isManageMode

  if (!setIsManageMode && controlBy === 'processDetail') {
    isManageModeDefault = processStore?.isManageModeInDetail
    showManageButton = processStore?.canUserEditInProcessDetail
  }
  if (!setIsManageMode && controlBy === 'userDetailPage') {
    const canEditUserDetail: boolean =
      currentUser.authRoleId === AuthRoleIdEnum.ORG_ADMIN ||
      currentUser.authRoleId < (userDetail?.authRoleId ?? AuthRoleIdEnum.BASIC_USER)
    isManageModeDefault = userStore?.isManageModeInUserDetail
    showManageButton = userStore?.canUserEditInUserDetail && canEditUserDetail
  }
  if (!setIsManageMode && controlBy === 'processList') {
    isManageModeDefault = processStore?.isManageModeInProcessList
    showManageButton = processStore?.canUserEditInProcessList
  }
  if (!setIsManageMode && controlBy === 'searchPage') {
    isManageModeDefault = searchStore?.isManageMode
    showManageButton = userCanManage
  }
  if (!setIsManageMode && controlBy === 'collectionPage') {
    isManageModeDefault = collectionStore?.isManageMode
    showManageButton = userCanManage
  }
  if (!setIsManageMode && controlBy === 'collectionDetailPage') {
    isManageModeDefault = collectionStore?.isManageMode
    if (isEditor) {
      showManageButton = isManagePermission
    } else {
      showManageButton = userCanManage
    }
  }
  if (!setIsManageMode && controlBy === 'archivePage') {
    isManageModeDefault = processStore?.isManageModeInArchive
    showManageButton = userCanManage
  }
  if (!setIsManageMode && controlBy === 'groupMemberList') {
    isManageModeDefault = groupStore?.isManageModeInMemberList
    showManageButton = userCanManage
  }
  if (!setIsManageMode && controlBy === 'userPage') {
    isManageModeDefault = userStore?.isManageMode
    showManageButton = !isBasicUser
  }
  if (!setIsManageMode && controlBy === 'commonLibraryList') {
    if (isBasicUser) {
      commonLibraryStore.isManageModeInList = false
    }
    isManageModeDefault = commonLibraryStore?.isManageModeInList
    showManageButton = !isBasicUser
  }
  if (!setIsManageMode && controlBy === 'plansPage') {
    isManageModeDefault = planStore?.isManageModeInPlansList
    showManageButton = !isBasicUser
  }
  if (!setIsManageMode && controlBy === 'workPlanDetail') {
    isManageModeDefault = planStore?.isManageModeInDetail
    showManageButton = isWorkPlanDetailManageModeEnabled ? !isBasicUser : false
  }

  useEffect(() => {
    if (isCommonStepEnabled) {
      commonLibraryStore.setFeatureEnabled(true)
    }
  }, [isCommonStepEnabled])

  return (
    <HStack background="gray.100" justifyContent="space-between" className={styles.headerContainer}>
      <HStack spacing={2} justifyContent="space-between">
        <HStack fontSize={{ base: 'sm', md: 'md' }} lineHeight={6} spacing={1}>
          <SvgIcon
            iconName="home"
            size={24}
            cursor="pointer"
            onClick={() => navigate(routes.dashboard.value, { replace: true })}
          />
          <ChevronRightIcon />
          {typeof title === 'string' ? (
            <Text fontFamily="Inter" color="gray.700">
              {title}
            </Text>
          ) : (
            title
          )}
        </HStack>
      </HStack>
      {(setIsManageMode || (controlBy && showManageButton)) && (
        <Button
          border={0}
          variant="outline"
          onClick={() => {
            if (controlBy === 'processDetail') {
              const next = !processStore?.isManageModeInDetail
              if (next) {
                const event = new CustomEvent('process_request_manage_enable', { detail: {} })
                window.dispatchEvent(event)
                return
              }
              processStore?.setManageModeInDetail(false)
            } else if (controlBy === 'processList') {
              processStore?.setManageModeInList(!processStore?.isManageModeInProcessList)
            } else if (controlBy === 'searchPage') {
              searchStore?.setManageMode(!searchStore?.isManageMode)
            } else if (controlBy === 'collectionPage') {
              collectionStore?.setManageMode(!collectionStore?.isManageMode)
            } else if (controlBy === 'collectionDetailPage') {
              collectionStore?.setManageMode(!collectionStore?.isManageMode, true)
            } else if (controlBy === 'archivePage') {
              processStore?.setManageModeInArchive(!processStore?.isManageModeInArchive)
            } else if (controlBy === 'groupMemberList') {
              groupStore?.setManageModeInMemberList(!groupStore?.isManageModeInMemberList)
            } else if (controlBy === 'userPage') {
              userStore?.setManageMode(!userStore?.isManageMode)
            } else if (controlBy === 'userDetailPage') {
              userStore?.setManageModeInUserDetail(!userStore?.isManageModeInUserDetail)
            } else if (controlBy === 'commonLibraryList') {
              commonLibraryStore?.setManageModeInList(!commonLibraryStore?.isManageModeInList)
            } else if (controlBy === 'plansPage') {
              planStore?.setManageModeInPlansList(!planStore?.isManageModeInPlansList)
            } else if (controlBy === 'workPlanDetail') {
              planStore?.setManageModeInDetail(!planStore?.isManageModeInDetail)
            } else {
              setIsManageMode && setIsManageMode(!isManageMode)
            }
          }}
          gap={{ base: 0, md: 2 }}
          padding={{ base: '10px', md: '16px' }}
          fontSize={{
            base: '0px',
            md: '16px'
          }}
          fontWeight={'500'}
          lineHeight={'24px'}
          color={'gray.700'}
          background={'whiteAlpha.700'}
          _hover={{
            background: 'gray.200'
          }}
          _active={{
            background: 'gray.300'
          }}
        >
          <Flex height={6} alignItems="center">
            {isManageModeDefault ? (
              <SvgIcon size={16} iconName="circle-check" color="gray.700" />
            ) : (
              <EditIcon className={styles.icon} />
            )}
          </Flex>
          {isManageModeDefault ? 'Done' : 'Manage'}
        </Button>
      )}
    </HStack>
  )
}

export default observer(HeaderBar)